import { useMemo, useState } from "react"
import type { ComponentProps, FormEvent } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Checkbox from "@/v2-ui/checkbox"
import Icon from "@/v2-ui/icon"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import { useDebouncedValue } from "@/v2-ui/utils/utils.debounce"
import Button from "@/v2-backoffice-client/components/button"
import {
  CRM_LOOKUP_DEBOUNCE_MS,
  CRM_LOOKUP_LIMIT,
} from "@/v2-backoffice-client/crm/crm.constants"
import {
  digitsOnly,
  hasSearchableDomain,
  hasSearchableNameToken,
  isSearchableOrgNumber,
} from "@/v2-backoffice-client/crm/crm.utils"
import { useCustomerCreateMatchesQuery } from "@/v2-backoffice-client/customer/create/customer.create.matches.query"
import CustomerCreateMatches from "@/v2-backoffice-client/customer/create/CustomerCreateMatches"
import type { CustomerMatchCandidate } from "@/v2-backoffice-client/customer/create/CustomerMatchList"
import {
  type CustomerCreateInput,
  type CustomerCrmDecision,
  findBlockingDuplicate,
  getCreateButtonLabel,
  prefillFromCompany,
} from "@/v2-backoffice-client/customer/create/customer.create.utils"
import type { CustomerCreateMutationVariables } from "@/v2-backoffice-client/customer/create/__types__/customer.create.mutation"

export type CustomerCreateFormData = {
  input: CustomerCreateInput
  crm: NonNullable<CustomerCreateMutationVariables["crm"]>
}

export type CustomerCreateFormProps = Omit<
  ComponentProps<"form">,
  "onSubmit"
> & {
  loading: boolean
  onSubmit(data: CustomerCreateFormData, e: FormEvent): void
  onCancel(): void
  onOpenCustomer(candidate: CustomerMatchCandidate): void
}

/**
 * The create-customer form with the duplicate check built in. As the
 * operator types a name, org.nr or website, Backoffice and HubSpot are
 * checked together and the hits shown right under the fields: an existing
 * customer is opened instead of re-created (an exact org.nr twin blocks the
 * button outright), a HubSpot-only company is selected so the customer is
 * created linked to it, and when nothing matches the default is to create
 * the company in HubSpot too — one checkbox to opt out. The submit button
 * always says which of the three will happen.
 */
function CustomerCreateForm(props: CustomerCreateFormProps) {
  const {
    loading,
    onSubmit,
    onCancel,
    onOpenCustomer,
    className,
    ...formProps
  } = props

  const [formData, setFormData] = useState<CustomerCreateInput>({ Name: "" })
  const [decision, setDecision] = useState<CustomerCrmDecision>({
    mode: "create",
  })

  function handleChange(field: keyof CustomerCreateInput, value: string) {
    // Empty optional fields stay undefined so the mutation input only
    // carries what the operator actually typed.
    setFormData({ ...formData, [field]: value === "" ? undefined : value })
  }

  // The lookup key: only the three fields the check reads, debounced as one
  // string so a keystroke in any of them restarts the same timer.
  const lookupKey = useDebouncedValue(
    [
      formData.Name ?? "",
      digitsOnly(formData.OrganizationNumber ?? ""),
      formData.Website ?? "",
    ].join("\u0000"),
    CRM_LOOKUP_DEBOUNCE_MS,
  )
  const [name, organizationNumber, website] = lookupKey.split("\u0000")
  const lookupActive =
    hasSearchableNameToken(name) ||
    isSearchableOrgNumber(organizationNumber) ||
    hasSearchableDomain(website)

  const {
    data,
    previousData,
    loading: checking,
  } = useCustomerCreateMatchesQuery({
    skip: !lookupActive,
    variables: {
      name: name || undefined,
      organizationNumber: organizationNumber || undefined,
      website: website || undefined,
      limit: CRM_LOOKUP_LIMIT,
    },
  })
  // Keep the last answer on screen while the next one loads — the list
  // must not flicker empty between keystrokes.
  const matches = (data ?? previousData)?.findCustomerMatches
  const candidates = useMemo(
    () => (lookupActive ? (matches?.Candidates ?? []) : []),
    [lookupActive, matches],
  )
  const blockingDuplicate = findBlockingDuplicate(candidates)

  // A selected company that dropped out of the results (the operator kept
  // typing) stays selected — it is the operator's explicit choice.
  const selectedCompany = decision.mode === "link" ? decision.company : null

  function selectCompany(candidate: CustomerMatchCandidate) {
    if (selectedCompany?.ExternalCompanyId === candidate.ExternalCompanyId) {
      setDecision({ mode: "create" })
      return
    }
    setDecision({ mode: "link", company: candidate })
    setFormData((current) => prefillFromCompany(current, candidate))
  }

  return (
    <form
      className={cn("customer-create-form space-y-4", className)}
      onSubmit={(e) => {
        e.preventDefault()
        if (blockingDuplicate) return
        onSubmit(
          {
            input: formData,
            crm:
              decision.mode === "link"
                ? { ExternalCompanyId: decision.company.ExternalCompanyId }
                : { CreateCompany: decision.mode === "create" },
          },
          e,
        )
      }}
      {...formProps}
    >
      {/* The check's verdict is pinned under the field and its list drops
          down over the fields below — the form never moves (CrmCheckedField). */}
      <CustomerCreateMatches
        active={lookupActive}
        loading={checking}
        answered={matches !== undefined}
        hubspotSearched={matches?.HubspotSearched ?? true}
        candidates={candidates}
        blockingDuplicate={blockingDuplicate}
        selectedCompany={selectedCompany}
        onSelectCompany={selectCompany}
        onClearSelection={() => {
          setDecision({ mode: "create" })
        }}
        onOpenCustomer={onOpenCustomer}
      >
        <Label htmlFor="customerName">Name</Label>
        <Input
          id="customerName"
          type="text"
          required
          autoFocus
          autoComplete="off"
          placeholder="Company name — checked against Backoffice and HubSpot as you type"
          value={formData.Name}
          onChange={(e) => {
            setFormData({ ...formData, Name: e.target.value })
          }}
        />
      </CustomerCreateMatches>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customerOrganizationNumber">Org.nr</Label>
          <Input
            id="customerOrganizationNumber"
            type="text"
            autoComplete="off"
            inputMode="numeric"
            placeholder="9 digits — exact matches are flagged"
            value={formData.OrganizationNumber ?? ""}
            onChange={(e) => {
              handleChange("OrganizationNumber", e.target.value)
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerWebsite">Website</Label>
          <Input
            id="customerWebsite"
            type="text"
            autoComplete="off"
            placeholder="acme.no"
            value={formData.Website ?? ""}
            onChange={(e) => {
              handleChange("Website", e.target.value)
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customerPhone">Phone</Label>
          <Input
            id="customerPhone"
            type="tel"
            value={formData.Phone ?? ""}
            onChange={(e) => {
              handleChange("Phone", e.target.value)
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerEmail">Email</Label>
          <Input
            id="customerEmail"
            type="email"
            value={formData.Email ?? ""}
            onChange={(e) => {
              handleChange("Email", e.target.value)
            }}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="customerStreet">Street</Label>
        <Input
          id="customerStreet"
          type="text"
          value={formData.Street ?? ""}
          onChange={(e) => {
            handleChange("Street", e.target.value)
          }}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customerPostalCode">Postal code</Label>
          <Input
            id="customerPostalCode"
            type="text"
            value={formData.PostalCode ?? ""}
            onChange={(e) => {
              handleChange("PostalCode", e.target.value)
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerCity">City</Label>
          <Input
            id="customerCity"
            type="text"
            value={formData.City ?? ""}
            onChange={(e) => {
              handleChange("City", e.target.value)
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerCountry">Country</Label>
          <Input
            id="customerCountry"
            type="text"
            value={formData.Country ?? ""}
            onChange={(e) => {
              handleChange("Country", e.target.value)
            }}
          />
        </div>
      </div>

      {/* The CRM decision: what happens in HubSpot when Create is pressed.
          Always two single lines (title + helper) at one height, whichever
          of the three states it is in — the block never grows or shrinks. */}
      <div className="customer-create-crm flex min-h-[3.25rem] items-center rounded-md border border-border px-3 text-sm">
        {blockingDuplicate ? (
          <div className="flex min-w-0 items-center gap-2">
            <Icon
              src="exclamation"
              size={14}
              className="shrink-0 text-destructive"
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">
                This org.nr already belongs to {blockingDuplicate.Name}
              </span>
              <span className="truncate text-muted-foreground">
                Open the existing customer instead — Create is disabled.
              </span>
            </div>
          </div>
        ) : selectedCompany ? (
          <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <Icon src="hubspot" size={14} className="shrink-0 text-primary" />
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-medium">
                  Will be linked to {selectedCompany.Name} in HubSpot
                </span>
                <span className="truncate text-muted-foreground">
                  Empty fields filled from it — edit freely, Backoffice owns
                  them.
                </span>
              </div>
            </div>
            <Button
              type="button"
              size="tiny"
              variant="ghost"
              onClick={() => {
                setDecision({ mode: "create" })
              }}
            >
              Change
            </Button>
          </div>
        ) : (
          <Label className="flex min-w-0 cursor-pointer items-center gap-2">
            <Checkbox
              checked={decision.mode === "create"}
              onCheckedChange={(checked) => {
                setDecision({ mode: checked === true ? "create" : "none" })
              }}
            />
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium">
                Also create this company in HubSpot
              </span>
              <span className="truncate font-normal text-muted-foreground">
                {decision.mode === "create"
                  ? "Sales sees it right away, linked to this customer, with you as owner."
                  : "Backoffice only — link a HubSpot company later from the customer panel."}
              </span>
            </span>
          </Label>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        {/* min-w keeps the button's edge still while its label changes. */}
        <Button
          type="submit"
          className="min-w-56"
          isLoading={loading}
          disabled={!!blockingDuplicate}
          title={
            blockingDuplicate
              ? "A customer with this org.nr already exists — open it instead"
              : undefined
          }
        >
          {loading
            ? "Creating..."
            : blockingDuplicate
              ? "Create"
              : getCreateButtonLabel(decision)}
        </Button>
      </div>
    </form>
  )
}

export default CustomerCreateForm
